/**
 * Typed RPC wrappers for the OpenClaw gateway.
 *
 * Each function wraps GatewayClient.request() with proper param/result types.
 * Types come from lexide/src/types/gateway.ts.
 */

import type { GatewayClient } from "./gateway-client";
import type {
  ChatSendParams, ChatSendResult,
  ChatAbortParams, ChatAbortResult,
  ChatHistoryParams, ChatHistoryResult,
  LxChatHistoryParams, LxChatHistoryResult,
  LxChatReadMediaParams, LxChatReadMediaResult,
  RuntimeLlmStatus,
  ClientOpenParams, ClientOpenResult,
  ClientCloseParams, ClientContextParams, ClientContextResult,
  ClientSetStatusParams, ClientSetStatusResult,
  ClientUpdateParams, ClientUpdateResult,
  ClientCreateParams, ClientCreateResult,
  ClientDeleteParams, ClientDeleteResult,
  MatterUpdateParams, MatterUpdateResult,
  MatterOpenParams, MatterOpenResult,
  MatterCloseParams, MatterContextParams, MatterContextResult,
  MatterSetStatusParams, MatterSetStatusResult,
  CaseOpenParams, CaseOpenResult,
  CaseCreateParams, CaseCreateResult,
  CaseReslugParams, CaseReslugResult,
  CaseAssignParams, CaseAssignResult,
  CaseLocateParams, CaseLocateResult,
  CaseWorkspaceTreeParams, CaseWorkspaceTreeResult,
  OrgWorkspaceTreeParams,
  CaseFolderMapParams, CaseFolderMapResult,
  OrgFolderMapParams,
  CaseSourceAssessmentParams, CaseSourceAssessmentResult,
  CaseSourceAssessSubmitParams, CaseSourceAssessSubmitResult,
  CaseSourceCatalogParams, CaseSourceCatalogResult,
  RuntimeSystemEnqueueEventParams, RuntimeSystemEnqueueEventResult,
  CaseCloseParams, CaseContextParams, CaseContextResult,
  CaseSetStatusParams, CaseSetStatusResult,
  ContractContextParams, ContractContextResult,
  AdvisoryContextParams, AdvisoryContextResult,
  DocumentListParams, DocumentReadParams, DocumentReadResult,
  OrgDocumentReadParams,
  DocumentReadBytesParams, DocumentReadBytesResult,
  OrgDocumentReadBytesParams,
  CaseDocumentDetailsParams, CaseDocumentDetailsResult,
  CaseDocumentNoteWriteParams, CaseDocumentNoteWriteResult,
  DocumentUploadParams, DocumentUploadResult, DocumentUploadLimits,
  DocumentCopyMoveParams, DocumentCopyMoveResult,
  DocumentDeleteParams, DocumentDeleteResult,
  DocumentCreateParams, DocumentCreateResult,
  DocumentSetStatusParams, DocumentSetStatusResult,
  DocumentSetDisplayParams, DocumentSetDisplayResult,
  DocumentListVersionsParams, DocumentListVersionsResult,
  DocumentReadVersionParams, DocumentReadVersionResult,
  DocumentReadPromptParams, DocumentReadPromptResult,
  PartyListParams, DeadlineListParams, TimelineListParams, StrictCollectionResult,
  CaseRefreshParams, CaseRefreshResult,
  PetitionReadParams, PetitionReadResult,
  PetitionUpdateParams, PetitionUpdateResult,
  SearchCaseParams, SearchMatterParams, SearchClientParams, SearchResult,
  OrgSearchParams,
  TaskSpawnParams, TaskSpawnResult,
  TaskCancelParams, TaskStatusParams, TaskStatus,
  ContextResolveParams, ContextResolveResult,
} from "@/types/gateway";
import type { Party } from "@/types/party";
import type {
  AssistantIdentity,
  Contact,
  ContactsArchiveParams,
  ContactsBindPeerParams,
  ContactsGetParams,
  ContactsGetResult,
  ContactsListParams,
  ContactsListResult,
  ContactsUnbindPeerParams,
  ContactsUpsertParams,
  ContactsUpsertResult,
  IdentityGetResult,
  IdentitySetParams,
  EmailRulesGetResult,
  EmailRulesSetParams,
  MailOwnerVerifyStartParams,
  MailOwnerVerifyStartResult,
  MailOwnerVerifyConfirmResult,
  MailOwnerVerifyStatusResult,
  PairOwnerPollResult,
  PairOwnerStartParams,
  PairOwnerStartResult,
  SetupStatus,
} from "@/types/contacts";
import type { Deadline } from "@/types/deadline";
import type { TimelineEvent } from "@/types/timeline";
import type { DocumentListItem } from "@/types/document";

/**
 * C.5 — portfolio (matter) + relationship (client) timeline entry
 * shape returned by `matter.timeline` / `client.timeline`. Mirrors
 * lx-kernel's `MatterTimelineEntry` / `ClientTimelineEntry` (same
 * shape, different file locations).
 */
export interface TimelineEntryRpc {
  timestamp: string;
  kind: string;
  summary: string;
  data?: Record<string, unknown>;
}

// Helper: convert typed params to Record<string, unknown> for the generic request method
function p(params: object): Record<string, unknown> {
  return params as Record<string, unknown>;
}

export function createGatewayRpc(client: GatewayClient) {
  return {
    /** Escape hatch for methods without a typed wrapper. */
    /** `timeoutMs` overrides the 30s default for methods that are legitimately
     *  slow server-side (e.g. `mail.*`, which shells out to himalaya per row). */
    call: <T>(method: string, params: Record<string, unknown> = {}, timeoutMs?: number) =>
      client.request<T>(method, params, timeoutMs),
    // Chat
    // 180s override (vs the 30s default): a reasoning-heavy concierge turn
    // (gpt-5.5-xhigh + file reads) legitimately runs 30–70s, so the default
    // timeout falsely rejects an in-progress turn as "provider unavailable".
    chatSend: (params: ChatSendParams) => client.request<ChatSendResult>("chat.send", p(params), 180_000),
    chatAbort: (params: ChatAbortParams) => client.request<ChatAbortResult>("chat.abort", p(params)),
    chatHistory: (params: ChatHistoryParams) => client.request<ChatHistoryResult>("chat.history", p(params)),
    /** Lexiun-owned conversation history — un-pruned reload/replay for the
     *  case/global MAIN chat (core chat.history drops text-only replies).
     *  See ai-docs/lexiun-conversation-history-plan.md. */
    lxChatHistory: (params: LxChatHistoryParams) =>
      client.request<LxChatHistoryResult>("lx.chat_history", p(params)),
    /** Serve a durable conversation-attachment file (the audio behind an IM
     *  voice-note transcript) — scope-bound like lx.chat_history. */
    lxChatReadMedia: (params: LxChatReadMediaParams) =>
      client.request<LxChatReadMediaResult>("lx.chat_read_media", p(params)),
    /** Promptbox voice dictation — ships mic-recorded audio (base64) to the
     *  runtime's LOCAL lx-stt (small whisper model, no audio egress) and
     *  returns the transcript for the composer. Operator brief 2026-07-02.
     *  90s timeout override: a cold whisper-model load can exceed the 30s
     *  client default (server-side lx-stt cap is 120s). */
    lxSttTranscribe: (params: { audioBase64: string; ext?: string; lang?: string }) =>
      client.request<{ transcript: string }>("lx.stt_transcribe", p(params), 90_000),
    /** Read-only "all model selections" status (LLM model-selection
     *  visibility, Phase 1). Read-only for all users (D2). */
    runtimeLlmStatus: () =>
      client.request<RuntimeLlmStatus>("runtime.llm_status", {}),

    // Client (D5b)
    clientOpen: (params: ClientOpenParams) => client.request<ClientOpenResult>("client.open", p(params)),
    clientClose: (params: ClientCloseParams) => client.request<{ ok: boolean }>("client.close", p(params)),
    clientContext: (params: ClientContextParams) => client.request<ClientContextResult>("client.context", p(params)),
    /** C.5 — relationship timeline entries (populated by C.6 propagation). */
    clientTimeline: (params: { clientId: string }) =>
      client.request<{ entries: TimelineEntryRpc[] }>("client.timeline", p(params)),

    // Matter (D5b)
    matterOpen: (params: MatterOpenParams) => client.request<MatterOpenResult>("matter.open", p(params)),
    matterClose: (params: MatterCloseParams) => client.request<{ ok: boolean }>("matter.close", p(params)),
    matterContext: (params: MatterContextParams) => client.request<MatterContextResult>("matter.context", p(params)),
    /** C.5 — portfolio timeline entries (populated by C.6 propagation). */
    matterTimeline: (params: { clientId: string; matterId: string }) =>
      client.request<{ entries: TimelineEntryRpc[] }>("matter.timeline", p(params)),

    // Case
    caseOpen: (params: CaseOpenParams) => client.request<CaseOpenResult>("case.open", p(params)),
    /** New-Case UI — scaffolds a case via workspace-keeper create-mode +
     *  registers it (runtime-shipped 2026-07-05). Relayed through lexserver like
     *  `case.workspace_tree`. Handoff: case-creation-lexide-handoff-2026-07-05.md. */
    caseCreate: (params: CaseCreateParams) => client.request<CaseCreateResult>("case.create", p(params)),
    /** RETIRE a case to the recycle bin. Soft-delete only — the workspace is
     *  moved to `.case-recycle-bin/`, never destroyed, and is restorable.
     *
     *  `force` is sent ALWAYS, and that is not a bypass. The flag guards
     *  `retireCaseFolder`'s non-empty check (`workspace-manager.ts:66`),
     *  which predates soft-delete: it existed to stop a *destructive* delete
     *  of a case that still had content. Retiring destroys nothing, and every
     *  real case has at least `.lexiun/`, so without it the affordance could
     *  never succeed on anything but an empty shell — the runtime's own error
     *  even instructs "Pass force=true to retire it to the recycle bin".
     *  The deliberateness the check used to provide now lives where it
     *  belongs: the org-admin gate (server-side) plus the typed-token dialog.
     *
     *  There is still no permanent-delete variant here. The one legitimate
     *  hard delete is the platform-admin purge, which lives on the LexPortal
     *  HTTP surface and nowhere else. */
    caseDelete: (params: { caseId: string }) =>
      client.request<{ deleted: boolean }>("case.delete", p({ ...params, force: true })),
    /** Deliberate, DRAIN-GATED rename of the case's on-disk workspace FOLDER
     *  slug — NOT the everyday display-name rename. Drains in-flight work on
     *  the case, then renames the folder; the immutable `caseId` is unchanged
     *  so the open view / URL survives. Callers rely on the `case.updated`
     *  `{kind:"reslugged"}` broadcast to refresh (no re-navigation). NEW runtime
     *  RPC — BUILDING runtime-side as S4 (handback §4); it 404s until S4 lands,
     *  so the exception-path UI must surface the error gracefully. */
    caseReslug: (params: CaseReslugParams) => client.request<CaseReslugResult>("case.reslug", p(params)),
    /** D-5 deferred-assignment — assign / reassign / unassign a FLAT case's parentage
     *  (the Unassigned-bucket "Assign" affordance). Tri-state clientId/matterId
     *  (string=set · null=clear · absent=leave; ≥1 required); coherence enforced
     *  runtime-side (a matter needs both ids together). Relayed through lexserver
     *  like `case.update`; emits `case.updated{kind:"reassigned"}`. Legacy cases
     *  are refused (flat-only). */
    caseAssign: (params: CaseAssignParams) => client.request<CaseAssignResult>("case.assign", p(params)),
    caseClose: (params: CaseCloseParams) => client.request<{ ok: boolean }>("case.close", p(params)),
    caseContext: (params: CaseContextParams) => client.request<CaseContextResult>("case.context", p(params)),
    /** §18.5 force-refresh. Resolves with `status` even on failure — see
     *  `CaseRefreshResult`; a `.catch` here only sees TRANSPORT errors. */
    caseRefresh: (params: CaseRefreshParams) =>
      client.request<CaseRefreshResult>("case.refresh", p(params)),
    /** D5a: short-URL bootstrap — resolves a bare caseId to {clientId, matterId, orgId}. */
    caseLocate: (params: CaseLocateParams) => client.request<CaseLocateResult>("case.locate", p(params)),
    /** CaseNavigator case-mode — real on-disk workspace folder/file tree
     *  (hidden/internal entries filtered server-side). */
    caseWorkspaceTree: (params: CaseWorkspaceTreeParams) =>
      client.request<CaseWorkspaceTreeResult>("case.workspace_tree", p(params)),
    /** CCM Wave 2 — org-scope ("Global" per-user workspace) tree.
     *  Sends `{scope:"org"}`; the runtime (R3, `9ed2d9e7`) resolves the
     *  owner from `LEX_USER_ID` and returns a case-shaped tree with NO
     *  `caseId`. Same `WorkspaceTreeFolder` shape — the existing
     *  `CaseFileTree`/`CaseFileNav` machinery renders it unchanged. */
    caseWorkspaceTreeOrg: (params: OrgWorkspaceTreeParams = { scope: "org" }) =>
      client.request<CaseWorkspaceTreeResult>("case.workspace_tree", p(params)),
    /** Presentation overlay for the workspace tree — friendly multilingual
     *  labels, emoji, order, visibility per folder. Joined onto
     *  `case.workspace_tree` results by `entry.key === folder.name`. See
     *  ai-docs/handoffs/folder-map-lexide-consumption-handoff.md. */
    caseFolderMap: (params: CaseFolderMapParams) =>
      client.request<CaseFolderMapResult>("case.folder_map", p(params)),
    /** CCM Wave 2 — org-scope presentation overlay. Sends `{scope:"org"}`;
     *  same friendly-labels/emoji/order/visibility envelope as the case
     *  variant (the org workspace shares the seed zone layout). */
    caseFolderMapOrg: (params: OrgFolderMapParams = { scope: "org" }) =>
      client.request<CaseFolderMapResult>("case.folder_map", p(params)),
    /** Part G — source assessment (`.info` sidecar). Returns the lawyer's
     *  role/weight/relevance/actors/notes for the source identified by
     *  `sourceId` (sha256, stable across re-ingest). The `.info` is HIDDEN
     *  from `case.workspace_tree`; this is the dedicated read path so the
     *  LexIDE assessment viewer can show + edit its content without
     *  exposing the file. See
     *  `ai-docs/handoffs/source-info-viewer-lexruntime-dep-handoff-2026-06-27.md`.
     *
     *  ⚠ **NO CALLER TODAY, and whoever adds the first one MUST pass
     *  `companionRef: row.companion_ref`** (verbatim from the
     *  `case.source_catalog` row the lawyer clicked). One `sourceId` — the
     *  sha256 of the original bytes — can name TWO files when the same
     *  document was ingested twice, and resolving by `sourceId` alone picks
     *  an ARBITRARY twin. The runtime accepts the ref additively
     *  (`mission-ingest` `a5a8a717c`): it derives the `.info`, rejects a
     *  malformed ref with INVALID_PARAMS, and NOT_FOUNDs one whose
     *  `source_id` disagrees — so passing it through needs no SPA-side
     *  validation, and OMITTING it silently restores the arbitrary-twin pick.
     *
     *  The pane does not use this today: `useSourceInfoFile` reads the
     *  sibling `<stem>.info.md` BY PATH, which is twin-correct by
     *  construction. This wrapper is the contract surface waiting for a
     *  future write control (handshake `20260811T220508Z`, countered +
     *  accepted). */
    caseSourceAssessment: (params: CaseSourceAssessmentParams) =>
      client.request<CaseSourceAssessmentResult>("case.source_assessment", p(params)),
    /** DEP #4 "option B" — the standalone assessment panel's write path.
     *  Funnels to the same kernel writer as the concierge `lx_source_assess`
     *  tool used by the §Q4 / awaitUser chat cards (no second-writer drift).
     *  Accepts re-assessment of an already-`assessed` source (LWW). See
     *  lexruntime commit `50a2cf06`.
     *
     *  ⚠ **NO CALLER TODAY — `AssessmentForm` is not mounted anywhere, and the
     *  re-evaluate control was REMOVED by operator ruling 2026-08-09** (see
     *  `SourceAssessmentPane`'s docblock, which forbids reintroducing one
     *  without a fresh ruling). So this is a write path with nothing wired to
     *  it, not a path that is merely unused.
     *
     *  ⚠ **Whoever wires the first caller MUST pass
     *  `companionRef: row.companion_ref`** — same reason as the read wrapper
     *  above, and it matters MORE here: without it a lawyer's verdict can be
     *  written onto the wrong duplicate of their own document. Additive
     *  runtime support shipped in `mission-ingest` `a5a8a717c`; omitting the
     *  field silently restores the arbitrary-twin write. */
    caseSourceAssessSubmit: (params: CaseSourceAssessSubmitParams) =>
      client.request<CaseSourceAssessSubmitResult>("case.source_assess_submit", p(params)),
    /** Reach path for the `index-builder`'s `_index.json` catalog —
     *  backs the source-row BADGE. NESTED `assessment` per row (NOT
     *  flat `info_*` keys); see `CaseSourceCatalogRow.assessment`.
     *  Staleness caveat: upload m-ingest mission doesn't rebuild the
     *  catalog today (lexruntime handback §6) — the GET picks up new
     *  sources only after the m-ingest-verify reconciler. */
    caseSourceCatalog: (params: CaseSourceCatalogParams) =>
      client.request<CaseSourceCatalogResult>("case.source_catalog", p(params)),
    /** Hidden SPA→concierge directive channel — the Claude-Code-style
     *  invisible-prompt mechanism. The `text` lands as a system event
     *  (role `custom_message` in the agent transcript), NEVER as a
     *  user-visible chat turn; only the concierge's response renders.
     *  Shipped 2026-06-28 (lexruntime `05db5028`). The case context
     *  is REQUIRED because the handler builds the case session key
     *  via `buildLxSessionKey({type:"case", …})`.
     *  Heartbeat caveat: dev runtime has heartbeat disabled, so the
     *  directive is observed on the concierge's NEXT turn, not
     *  proactively (handback §4). */
    runtimeSystemEnqueueEvent: (params: RuntimeSystemEnqueueEventParams) =>
      client.request<RuntimeSystemEnqueueEventResult>(
        "runtime.system.enqueue_event",
        p(params),
      ),
    /** C.5 — contract-matter clauses + obligations (reads 03_clauses/clauses.yaml + 04_obligations/obligations.yaml). */
    caseContractContext: (params: ContractContextParams) =>
      client.request<ContractContextResult>("case.contract_context", p(params)),
    /** C.5 — advisory-matter research trail + authorities (reads 03_research/research.yaml + 04_authorities/authorities.yaml). */
    caseAdvisoryContext: (params: AdvisoryContextParams) =>
      client.request<AdvisoryContextResult>("case.advisory_context", p(params)),
    /** C.6.deferred — set case status + propagate case.closed / case.status_changed to parent timelines. */
    caseSetStatus: (params: CaseSetStatusParams) =>
      client.request<CaseSetStatusResult>("case.set_status", p(params)),
    /** C.6.deferred — set matter status + propagate matter.closed / matter.status_changed to client timeline. */
    matterSetStatus: (params: MatterSetStatusParams) =>
      client.request<MatterSetStatusResult>("matter.set_status", p(params)),
    /** C.7.deferred — set client status + self-timeline entry + client.updated broadcast. */
    clientSetStatus: (params: ClientSetStatusParams) =>
      client.request<ClientSetStatusResult>("client.set_status", p(params)),
    /** Client name + `@alias` handle edit (client.update). The server COERCES the
     *  alias to the `@handle` grammar + dedupes per org, so the returned `alias`
     *  MAY differ from what was sent; a `client.updated` broadcast fires. Backend
     *  shipped on wip 2026-07-06 (client @alias handoff). */
    clientUpdate: (params: ClientUpdateParams) =>
      client.request<ClientUpdateResult>("client.update", p(params)),
    /** Create a client workspace + register it (client.create). Self-clients are
     *  auto-provisioned and rejected here. Fires `client.updated` if the runtime
     *  adds a created broadcast; the SPA also invalidates `["clients"]`. */
    clientCreate: (params: ClientCreateParams) =>
      client.request<ClientCreateResult>("client.create", p(params)),
    /** Client HARD delete — the platform's ONE ruled exception to the
     *  no-hard-delete rule (operator 2026-07-30 v2; plan
     *  ai-docs/lexide-navigation-and-client-manager-miniplan.md §2.4). The
     *  runtime calls lexserver FIRST, which UNASSIGNS every referencing case
     *  (live + recycle-binned) to «Não atribuídos» and 409s if any
     *  legacy-storageShape case exists — the guard is server-side; the SPA's
     *  census pre-check is UX, not enforcement. Do NOT call outside the
     *  ClientDeleteConfirm flow. Wire shape confirmed by the P2 handback
     *  (ac2ec7f3f) — see ClientDeleteResult. */
    clientDelete: (params: ClientDeleteParams) =>
      client.request<ClientDeleteResult>("client.delete", p(params)),
    /** Matter rename — displayNames-only (INV-3; slug/path never move). Handle
     *  `unchanged:true` as success-no-op, not failure. Shipped runtime-side
     *  before N2 asked for it — see the P2 handback. */
    matterUpdate: (params: MatterUpdateParams) =>
      client.request<MatterUpdateResult>("matter.update", p(params)),

    // Document
    documentList: (params: DocumentListParams) => client.request<DocumentListItem[]>("document.list", p(params)),
    documentRead: (params: DocumentReadParams) => client.request<DocumentReadResult>("document.read", p(params)),
    /** CCM Wave 2 — org-scope document read (`{scope:"org", docId}`). */
    documentReadOrg: (params: OrgDocumentReadParams) =>
      client.request<DocumentReadResult>("document.read", p(params)),
    /** `document.read_bytes` — raw workspace-relative file bytes (base64).
     *  Drives the `WorkspaceFilePane` viewers (PDF/image/text) per
     *  ai-docs/lexide-layout-plan.md §3.5. Backend: lexruntime commit `a5c3bf7b`. */
    documentReadBytes: (params: DocumentReadBytesParams) =>
      client.request<DocumentReadBytesResult>("document.read_bytes", p(params)),
    /** CCM Wave 2 — org-scope byte read (`{scope:"org", path}`). The
     *  byte/viewer path that opens an org-workspace file exactly like a
     *  case file. Result shape identical. */
    documentReadBytesOrg: (params: OrgDocumentReadBytesParams) =>
      client.request<DocumentReadBytesResult>("document.read_bytes", p(params)),
    /** A-14 — parent-keyed details read (provenance + notes INLINE, R6-pinned:
     *  one call renders the whole Detalhes pane; the SPA never names a
     *  sidecar). Contract: a14-kernel-half-runtime-miniplan.md §6. */
    caseDocumentDetailsRead: (params: CaseDocumentDetailsParams) =>
      client.request<CaseDocumentDetailsResult>("case.document_details_read", p(params)),
    /** A-14 — the ONLY door that writes a user note (kernel computes the
     *  sidecar; stamps `authored_by: user`; refuses when the parent is absent). */
    caseDocumentNoteWrite: (params: CaseDocumentNoteWriteParams) =>
      client.request<CaseDocumentNoteWriteResult>("case.document_note_write", p(params)),
    documentUpload: (params: DocumentUploadParams) => client.request<DocumentUploadResult>("document.upload", p(params)),
    /** Read-only platform policy driving the picker's `accept` list and the
     *  client-side pre-validation. Static — fetch once per session. */
    documentUploadLimits: () =>
      client.request<DocumentUploadLimits>("document.upload_limits", {}),
    /** CCM Wave 3 (R4, lexruntime `ce359f0f`) — copy a `sources/` resource
     *  (blob + its full companion set) into another scope. The `from` REQUIRES
     *  a `path`; the `to` is a scope address ONLY (the dest keeps the source's
     *  canonical `sources/<name>` ref). Allowed: `case→case`, `org→case`
     *  (promotion), `case→org`. The backend carries the sidecars, re-stamps
     *  provenance, and reindexes the destination catalog. */
    documentCopy: (params: DocumentCopyMoveParams) =>
      client.request<DocumentCopyMoveResult>("document.copy", p(params)),
    /** CCM Wave 3 (R4) — move = copy-then-delete-source (the destination set
     *  is written + verified FIRST, so a move can never lose the resource).
     *  Same envelope as `documentCopy`; on a `sources/` resource the SOURCE
     *  catalog is also re-derived (`reindexed.from:true`). */
    documentMove: (params: DocumentCopyMoveParams) =>
      client.request<DocumentCopyMoveResult>("document.move", p(params)),
    /**
     * Acervo delete (2026-08-01) — remove a `sources/` resource and its ENTIRE
     * member set from a case. Case scope only; case OWNER (`LEAD`) only;
     * `confirmed:true` required.
     *
     * ⚠ Unlike `document.set_display`, business outcomes do NOT ride the success
     * frame: every refusal comes back on the gateway ERROR envelope, so this
     * REJECTS with a `GatewayRpcError` whose `code` is the discriminator
     * (`FORBIDDEN` / `NOT_FOUND` / `FAILED_PRECONDITION` / `INVALID_PARAMS`).
     * Map it with `deleteErrorKey` — never by matching the message prose.
     */
    documentDelete: (params: DocumentDeleteParams) =>
      client.request<DocumentDeleteResult>("document.delete", p(params)),

    // AIEditor documents (Phase 5.2b.2)
    documentCreate: (params: DocumentCreateParams) =>
      client.request<DocumentCreateResult>("document.create", p(params)),
    documentSetStatus: (params: DocumentSetStatusParams) =>
      client.request<DocumentSetStatusResult>("document.set_status", p(params)),
    /** Workspace-file-aliases (P2): set/clear a file or folder's user-facing
     *  alias + description overlay (the on-disk name never changes). Business
     *  outcomes (conflict/invalid/forbidden) ride the success frame — switch on
     *  `result.ok`. Scope: the case triple OR `{scope:"org"}` (D10). */
    documentSetDisplay: (params: DocumentSetDisplayParams) =>
      client.request<DocumentSetDisplayResult>("document.set_display", p(params)),
    documentListVersions: (params: DocumentListVersionsParams) =>
      client.request<DocumentListVersionsResult>("document.list_versions", p(params)),
    documentReadVersion: (params: DocumentReadVersionParams) =>
      client.request<DocumentReadVersionResult>("document.read_version", p(params)),
    documentReadPrompt: (params: DocumentReadPromptParams) =>
      client.request<DocumentReadPromptResult>("document.read_prompt", p(params)),

    // Data queries
    partyList: (params: PartyListParams) => client.request<Party[]>("party.list", p(params)),
    deadlineList: (params: DeadlineListParams) => client.request<Deadline[]>("deadline.list", p(params)),
    timelineList: (params: TimelineListParams) => client.request<TimelineEvent[]>("timeline.list", p(params)),

    // ── STRICT data queries (D4) ──────────────────────────────────────────
    // The plain readers above collapse a CORRUPT file into a successful `[]`.
    // They do not reject, so no amount of `.catch` around them can tell "no
    // deadlines" apart from "deadlines.json is unparseable" — which is how a
    // header can calmly report "sem prazos" on a case with a prazo in three
    // days. These siblings return `{items, unreadable}`, where `unreadable`
    // names the files that could not be read, so a consumer drawing a
    // CONCLUSION from emptiness can check first.
    //
    // Use these ANYWHERE an absence is asserted. The plain ones remain correct
    // for rendering a list (an empty list looks the same either way).
    partyListStrict: (params: PartyListParams) =>
      client.request<StrictCollectionResult<Party>>("party.list_strict", p(params)),
    deadlineListStrict: (params: DeadlineListParams) =>
      client.request<StrictCollectionResult<Deadline>>("deadline.list_strict", p(params)),
    timelineListStrict: (params: TimelineListParams) =>
      client.request<StrictCollectionResult<TimelineEvent>>("timeline.list_strict", p(params)),
    petitionRead: (params: PetitionReadParams) => client.request<PetitionReadResult>("petition.read", p(params)),
    petitionUpdate: (params: PetitionUpdateParams) => client.request<PetitionUpdateResult>("petition.update", p(params)),

    // Search
    searchCase: (params: SearchCaseParams) => client.request<SearchResult[]>("search.case", p(params)),
    /** CCM Wave 2 — org-scope FTS (`search.case {scope:"org", query}`). */
    searchCaseOrg: (params: OrgSearchParams) =>
      client.request<SearchResult[]>("search.case", p(params)),
    /** Phase F — federated search across a matter's cases (cascade-access gated). */
    searchMatter: (params: SearchMatterParams) =>
      client.request<SearchResult[]>("search.matter", p(params)),
    /** Phase F — federated search across a client's matters + cases (cascade-access gated). */
    searchClient: (params: SearchClientParams) =>
      client.request<SearchResult[]>("search.client", p(params)),

    // Tasks
    taskSpawn: (params: TaskSpawnParams) => client.request<TaskSpawnResult>("task.spawn", p(params)),
    taskCancel: (params: TaskCancelParams) => client.request<{ ok: boolean; cancelled: boolean }>("task.cancel", p(params)),
    taskStatus: (params: TaskStatusParams) => client.request<TaskStatus>("task.status", p(params)),
    taskList: () => client.request<TaskStatus[]>("task.list", {}),

    // Context
    contextResolve: (params: ContextResolveParams) => client.request<ContextResolveResult>("context.resolve", p(params)),

    // Contacts + identity + setup (lexiun-contacts C1 contract —
    // ai-docs/handoffs/contacts-identity-ui-lexide-handoff-2026-07-15.md §3.
    // Methods land runtime-side under [M:contacts-C1-api]; until then callers
    // degrade gracefully on method-not-found).
    contactsList: (params: ContactsListParams = {}) =>
      client.request<ContactsListResult>("contacts.list", p(params)),
    contactsGet: (params: ContactsGetParams) =>
      client.request<ContactsGetResult>("contacts.get", p(params)),
    contactsUpsert: (params: ContactsUpsertParams) =>
      client.request<ContactsUpsertResult>("contacts.upsert", p(params)),
    contactsArchive: (params: ContactsArchiveParams) =>
      client.request<{ contact: Contact }>("contacts.archive", p(params)),
    contactsBindPeer: (params: ContactsBindPeerParams) =>
      client.request<{ contact: Contact }>("contacts.bind_peer", p(params)),
    contactsUnbindPeer: (params: ContactsUnbindPeerParams) =>
      client.request<{ contact: Contact }>("contacts.unbind_peer", p(params)),
    identityGet: () => client.request<IdentityGetResult>("identity.get", {}),
    identitySet: (params: IdentitySetParams) =>
      client.request<{ owner: Contact; assistant: AssistantIdentity }>("identity.set", p(params)),
    setupStatus: () => client.request<SetupStatus>("setup.status", {}),
    /** D-13 — record "the reminder was shown now" (runtime-persisted so
     *  devices agree). */
    setupReminderShown: () => client.request<SetupStatus>("setup.reminder_shown", {}),
    /** D-13 — the explicit "não me lembrar" toggle (auto-resets in 7 days). */
    setupReminderDisable: (params: { disabled: boolean }) =>
      client.request<SetupStatus>("setup.reminder_disable", p(params)),
    /** user-setup §5.1 — record/clear a step override (skip/defer/disable).
     *  Blocking steps are rejected runtime-side (FORBIDDEN — US-INV-5). */
    setupAck: (params: { stepId: string; ack: "skipped" | "deferred" | "disabled" | null }) =>
      client.request<SetupStatus>("setup.ack", p(params)),
    /** §4A.3-bis EMAIL_RULES (adopted face 2026-07-16; real store lands with
     *  [M:email-E0-bake]). */
    emailRulesGet: () => client.request<EmailRulesGetResult>("email.rules_get", {}),
    emailRulesSet: (params: EmailRulesSetParams) =>
      client.request<EmailRulesGetResult>("email.rules_set", p(params)),
    /** §3.9 block ③ — EP2 owner-email pairing (mailsystem B2, `bb3ca197c`).
     *  `start` SENDS a real e-mail from the org's assistant mailbox, so it is
     *  an action with a cost, not a poll: the server enforces a 60s resend
     *  cooldown (`VERIFY_RESEND_TOO_SOON` + `retryAfterSeconds`) so a
     *  double-click is REFUSED rather than sent twice. The 6-digit code never
     *  crosses the boundary — only `expiresAt`, for the countdown. */
    mailOwnerVerifyStart: (params: MailOwnerVerifyStartParams) =>
      client.request<MailOwnerVerifyStartResult>("mail.owner_verify_start", p(params)),
    /** Five wrong guesses BURN the code (`VERIFY_TOO_MANY_ATTEMPTS`), after
     *  which even the correct one fails and a fresh `start` is required —
     *  this code is checked by a directly-callable gateway method, unlike the
     *  channel-pairing code that must arrive over an inbound gate. */
    mailOwnerVerifyConfirm: (params: { code: string }) =>
      client.request<MailOwnerVerifyConfirmResult>("mail.owner_verify_confirm", p(params)),
    /** READ-ONLY — the ONLY B2 verb safe to call unprompted. Doubles as the
     *  block ③ support probe and as remount recovery for an in-flight code. */
    mailOwnerVerifyStatus: () =>
      client.request<MailOwnerVerifyStatusResult>("mail.owner_verify_status", {}),
    pairOwnerStart: (params: PairOwnerStartParams) =>
      client.request<PairOwnerStartResult>("channels.pair_owner.start", p(params)),
    pairOwnerPoll: (params: { channel: string }) =>
      client.request<PairOwnerPollResult>("channels.pair_owner.poll", p(params)),
  };
}

export type GatewayRpc = ReturnType<typeof createGatewayRpc>;
